/**
 * Admin: preview an emoji at every platform's exact size, with toggles
 * to exclude platforms where the glyph doesn't read well.
 *
 * Why a dedicated component instead of inline state inside the parent
 * EmojiManagerTab? The dialog has its own loading lifecycle, dirty-state,
 * and (de)bounced save — keeping it isolated also makes it trivial to
 * mount from elsewhere later (e.g. a "review uploads" admin page).
 *
 * Backend dependency:
 *   GET  /api/admin/emojis/{id}/preview/{platform}  → PNG (live resize)
 *   GET  /api/emojis/platform-specs                  → label/size/budget metadata
 *   PUT  /api/admin/emojis/{id}                      → save excluded_platforms
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Loader2, Save, X, ImageOff, Sparkles, Wand2 } from 'lucide-react';
import { apiFetch } from '@/hooks/useFetch';
import { toast } from 'sonner';
import AuthImg from '@/components/AuthImg';

export const EmojiPlatformPreviewDialog = ({ emoji, open, onOpenChange, onSaved }) => {
  const [specs, setSpecs] = useState([]);
  const [excluded, setExcluded] = useState([]);  // platform keys
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bustKey, setBustKey] = useState(0);  // cache-bust for previews
  // Auto-detect state — when the admin clicks the wand, we POST to the
  // backend heuristic endpoint and stash the suggestions here so the
  // banner can render. `null` = not run yet; `[]` = run, nothing flagged.
  const [autoDetecting, setAutoDetecting] = useState(false);
  const [autoSuggestions, setAutoSuggestions] = useState(null);

  // Load platform spec metadata once + seed local exclusion state from
  // the emoji record. `bustKey` forces every <img> to refetch when the
  // user opens the dialog after editing the source image.
  useEffect(() => {
    if (!open || !emoji) return undefined;
    let cancelled = false;
    setLoading(true);
    setExcluded(emoji.excluded_platforms || []);
    setBustKey(Date.now());
    (async () => {
      const { data } = await apiFetch('/api/emojis/platform-specs');
      if (cancelled) return;
      setSpecs(data?.platforms || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [open, emoji]);

  // Reset auto-suggestions whenever the dialog closes — keeps logic out
  // of the load-effect (linter complains about cross-state mutation in
  // a single effect).
  useEffect(() => {
    if (!open) setAutoSuggestions(null);
  }, [open]);

  const togglePlatform = useCallback((key) => {
    setExcluded((prev) => prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]);
  }, []);

  const dirty = useMemo(() => {
    const initial = new Set(emoji?.excluded_platforms || []);
    const current = new Set(excluded);
    if (initial.size !== current.size) return true;
    return [...initial].some((k) => !current.has(k));
  }, [emoji, excluded]);

  const handleSave = async () => {
    if (!emoji) return;
    setSaving(true);
    const { error } = await apiFetch(`/api/admin/emojis/${emoji.emoji_id}`, {
      method: 'PUT',
      body: { excluded_platforms: excluded },
    });
    setSaving(false);
    if (error) {
      toast.error(error);
      return;
    }
    toast.success(`Saved. ${excluded.length === 0 ? 'All' : (13 - excluded.length)} platforms enabled.`);
    onSaved?.(emoji.emoji_id, excluded);
    onOpenChange(false);
  };

  // Auto-detect: run the backend edge-density heuristic and stash
  // suggestions. Doesn't mutate anything by itself — the admin still
  // has to click "Apply suggestions" to merge the suggested keys into
  // the local `excluded` state.
  const handleAutoDetect = useCallback(async () => {
    if (!emoji) return;
    setAutoDetecting(true);
    const { data, error } = await apiFetch(
      `/api/admin/emojis/${emoji.emoji_id}/auto-detect-bad-platforms`,
      { method: 'POST', body: {} },
    );
    setAutoDetecting(false);
    if (error) {
      toast.error(error);
      return;
    }
    setAutoSuggestions(data?.suggested_exclusions || []);
    if ((data?.suggested_exclusions || []).length === 0) {
      toast.success('No platforms flagged — this glyph reads well everywhere.');
    } else {
      toast.info(`Heuristic flagged ${data.suggested_exclusions.length} platform(s) where this glyph may not read well.`);
    }
  }, [emoji]);

  const applySuggestions = useCallback(() => {
    if (!autoSuggestions || autoSuggestions.length === 0) return;
    setExcluded((prev) => {
      const next = new Set(prev);
      autoSuggestions.forEach((k) => next.add(k));
      return Array.from(next);
    });
    toast.success(`Auto-toggled off ${autoSuggestions.length} platform(s). Review and Save when ready.`);
    setAutoSuggestions(null);  // clear the banner
  }, [autoSuggestions]);

  if (!emoji) return null;

  // Group platforms by kind for cleaner visual layout.
  const emojiPlatforms = specs.filter((p) => p.kind === 'emoji');
  const stickerPlatforms = specs.filter((p) => p.kind === 'sticker');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col"
        data-testid="emoji-preview-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00A896]" />
            Preview: {emoji.label}
          </DialogTitle>
          <DialogDescription>
            How this emoji actually renders on each platform — at the exact size
            users will see. Toggle off any variants where the glyph pixelates,
            loses detail, or feels off-brand. Users will see those exclusions on
            the gallery tile before they pick the emoji.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#00A896]" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto space-y-4 px-1">
            {/* Auto-detect banner: stays at the top of the scroll area
                until dismissed or applied. */}
            <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-md border border-purple-500/30 bg-purple-500/5">
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-purple-700 dark:text-purple-300 flex items-center gap-1.5">
                  <Wand2 className="h-3.5 w-3.5" /> Auto-detect bad platforms
                </p>
                <p className="text-xs text-muted-foreground">
                  Runs an edge-density &amp; contrast-loss heuristic on every variant. Flags platforms where this glyph likely won&apos;t read well. Suggestion only — you still review &amp; save.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-purple-500/40 text-purple-700 dark:text-purple-300 hover:bg-purple-500/10"
                onClick={handleAutoDetect}
                disabled={autoDetecting}
                data-testid="emoji-auto-detect-btn"
              >
                {autoDetecting
                  ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Analysing…</>
                  : <><Wand2 className="h-3.5 w-3.5 mr-1" /> Run auto-detect</>}
              </Button>
            </div>

            {/* Suggestions banner — only renders after autoDetect runs */}
            {autoSuggestions !== null && autoSuggestions.length > 0 && (
              <div
                className="p-3 rounded-md border-2 border-amber-500/40 bg-amber-500/10 flex items-start justify-between gap-3 flex-wrap"
                data-testid="emoji-auto-suggestions-banner"
              >
                <div className="flex-1 min-w-[200px]">
                  <p className="text-sm font-semibold text-amber-700 dark:text-amber-300">
                    Heuristic suggests excluding {autoSuggestions.length} platform{autoSuggestions.length === 1 ? '' : 's'}:
                  </p>
                  <p className="text-xs text-amber-700/80 dark:text-amber-200/80 mt-0.5">
                    {autoSuggestions.map((k) => specs.find((s) => s.key === k)?.label || k).join(' · ')}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-amber-700 dark:text-amber-300"
                    onClick={() => setAutoSuggestions(null)}
                    data-testid="emoji-auto-dismiss-btn"
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-500 text-white hover:bg-amber-600"
                    onClick={applySuggestions}
                    data-testid="emoji-auto-apply-btn"
                  >
                    Apply suggestions
                  </Button>
                </div>
              </div>
            )}

            <PlatformGroup
              title="Emoji platforms"
              subtitle="Small inline chat reactions"
              specs={emojiPlatforms}
              excluded={excluded}
              onToggle={togglePlatform}
              emoji={emoji}
              bustKey={bustKey}
            />
            <PlatformGroup
              title="Sticker platforms"
              subtitle="Larger reaction images"
              specs={stickerPlatforms}
              excluded={excluded}
              onToggle={togglePlatform}
              emoji={emoji}
              bustKey={bustKey}
            />
          </div>
        )}

        <DialogFooter className="flex flex-row items-center justify-between border-t pt-3">
          <p className="text-xs text-muted-foreground">
            {excluded.length === 0
              ? 'All 13 platforms enabled'
              : `${excluded.length} platform${excluded.length === 1 ? '' : 's'} excluded`}
          </p>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              data-testid="emoji-preview-close-btn"
            >
              <X className="h-4 w-4 mr-1" /> Close
            </Button>
            <Button
              onClick={handleSave}
              disabled={!dirty || saving}
              className="bg-[#00A896] text-white hover:bg-[#008b7a]"
              data-testid="emoji-preview-save-btn"
            >
              {saving
                ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Saving…</>
                : <><Save className="h-4 w-4 mr-1" /> Save exclusions</>}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


/**
 * Internal: render one "Emoji" or "Sticker" group of platform tiles.
 * Each tile is a true <img> pointing at the live-resize endpoint so the
 * admin sees exactly what users will receive.
 */
const PlatformGroup = ({ title, subtitle, specs, excluded, onToggle, emoji, bustKey }) => {
  if (specs.length === 0) return null;
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </h3>
          <p className="text-xs text-muted-foreground/70">{subtitle}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {specs.map((p) => {
          const isOut = excluded.includes(p.key);
          // Real pixel size on the rendered tile. Cap the visible
          // preview at 128 so 512px sticker variants don't dominate
          // the dialog; admin can hover/zoom for full fidelity later.
          const previewPx = Math.min(p.size[0], 128);
          return (
            <div
              key={p.key}
              className={`relative rounded-md border-2 p-3 transition ${
                isOut
                  ? 'border-red-300 bg-red-500/5 dark:border-red-700/50'
                  : 'border-[#00A896]/20 bg-[#00E5CC]/5 hover:border-[#00A896]/40'
              }`}
              data-testid={`platform-preview-${p.key}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2">
                <Label
                  htmlFor={`toggle-${p.key}`}
                  className="text-xs font-medium leading-tight cursor-pointer flex-1"
                  title={p.label}
                >
                  {p.label}
                </Label>
                <Switch
                  id={`toggle-${p.key}`}
                  checked={!isOut}
                  onCheckedChange={() => onToggle(p.key)}
                  data-testid={`platform-toggle-${p.key}`}
                />
              </div>

              <div
                className="bg-[radial-gradient(circle,_rgba(0,229,204,0.05)_1px,_transparent_1px)] [background-size:8px_8px] rounded flex items-center justify-center mb-1"
                style={{ height: '136px' }}
              >
                {isOut ? (
                  <div className="text-center text-red-500/80 text-xs">
                    <ImageOff className="h-6 w-6 mx-auto mb-1 opacity-60" />
                    Excluded
                  </div>
                ) : (
                  /* AuthImg fetches via Bearer + cookie so admin previews
                     work even when the session cookie has been dropped
                     (the case behind the "all resized images broken"
                     production report). It also surfaces the backend's
                     real error so the admin sees e.g. "Source file not
                     found" → "click Delete broken only + re-upload". */
                  <AuthImg
                    url={`/api/admin/emojis/${emoji.emoji_id}/preview/${p.key}?v=${bustKey}`}
                    alt={`${emoji.label} on ${p.label}`}
                    style={{ width: `${previewPx}px`, height: `${previewPx}px` }}
                    className="object-contain"
                  />
                )}
              </div>

              <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                <span>{p.size[0]}×{p.size[1]} px</span>
                <Badge variant="outline" className="text-[10px] py-0 px-1.5">
                  ≤{p.byte_budget_kb} KB
                </Badge>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EmojiPlatformPreviewDialog;
